import React, { Fragment, useState, useEffect } from 'react';
import { Link, useParams, useMatch, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createLicense, getLicenseById } from '../../actions/license';
import { getEntities } from '../../actions/entity';
import Spinner from '../layout/Spinner';
import { useTranslation, Trans } from 'react-i18next';
import { TbLicense } from 'react-icons/tb';

const initialState = {
  licenseId: '',
  entity: '',
  type: '',
  startDate: '',
  endDate: '',
  status: '',
  descriptionADV: '',
  descriptionAdminEntity: ''
};

const LicenseEdit = ({
  auth,
  license: { license, loading },
  entity: { entities },
  createLicense,
  getLicenseById,
  getEntities,
  history
}) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState(initialState);
  const [action, setaction] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const [showHelp, setShowHelp] = useState(false);
  const [isValidImei, setIsValidImei] = useState(true);

  const creatingLicense = useMatch('/create-license');

  useEffect(() => {
    if (!license && !creatingLicense) {
      getLicenseById(id);
    }
    if (!loading) {
      if (!creatingLicense) {
        const licenseData = { ...initialState };
        for (const key in license) {
          if (key in licenseData) licenseData[key] = license[key];
        }
        // Conversion des dates en format YYYY-MM-DD
        if (license.startDate) {
          licenseData.startDate = license.startDate.split('T')[0];
        }
        if (license.endDate) {
          licenseData.endDate = license.endDate.split('T')[0];
        }

        const imeiRegex = /^[0-9]{15}$/;
        setIsValidImei(imeiRegex.test(license.licenseId));
        setFormData(licenseData);
      } else {
        if (auth.user.rights === 'admin' || auth.user.rights === 'ADV') {
          const licenseData = { ...initialState };

          // Initialisation de startDate comme un objet Date
          licenseData.startDate = new Date();

          // Copie de startDate pour endDate
          licenseData.endDate = new Date(licenseData.startDate);

          // Ajout de 365 jours à endDate
          licenseData.endDate.setFullYear(
            licenseData.endDate.getFullYear() + 1
          );

          // Formatage des dates au format 'YYYY-MM-DD' pour le formulaire
          licenseData.startDate = licenseData.startDate
            .toISOString()
            .split('T')[0];
          licenseData.endDate = licenseData.endDate.toISOString().split('T')[0];

          setFormData(licenseData);
        }
      }
    }
  }, [loading, license, id, getLicenseById, creatingLicense]);

  useEffect(() => {
    if (auth.user.rights === 'admin' || auth.user.rights === 'ADV') {
      getEntities();
    }
  }, [getEntities, auth.user.rights]);

  const {
    licenseId,
    entity,
    type,
    startDate,
    endDate,
    descriptionADV,
    status,
    descriptionAdminEntity
  } = formData;

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();

    const res = await createLicense(
      license ? license._id : null,
      formData,
      history
    );

    if (res && res.status === 200) {
      setaction(true);
      if (creatingLicense) {
        setTimeout(() => {
          navigate('/licenses');
        }, 5000);
      } else {
        setTimeout(() => {
          navigate('/licenses');
        }, 1000);
      }
    }
  };
  return (
    <section className="container">
      <Fragment>
        {action ||
        loading ||
        (entities.length === 0 &&
          (auth.user.rights === 'admin' || auth.user.rights === 'ADV')) ? (
          <div>
            <Spinner />
          </div>
        ) : (
          <Fragment>
            {auth.user.rights === 'admin' ||
            auth.user.rights === 'admin_entity' ||
            auth.user.rights === 'ADV' ? (
              <Fragment>
                <h1 className="large text-ascorel">
                  {creatingLicense ? (
                    <Trans>{t('create_license')}</Trans>
                  ) : (
                    <Trans>{t('mod_license_title')}</Trans>
                  )}
                </h1>
                <p className="lead">
                  <TbLicense />
                  {creatingLicense ? (
                    <Trans>{t('creer_license_lead')}</Trans>
                  ) : (
                    <Trans>{t('mod_license_lead')}</Trans>
                  )}
                </p>
                <small>{t('champs_requis')}</small>
                <form className="form" onSubmit={onSubmit}>
                  {auth.user.rights === 'admin' ||
                  auth.user.rights === 'ADV' ? (
                    <>
                      <div className="form-group">
                        <input
                          type="text"
                          placeholder={t('example_imei')}
                          name="licenseId"
                          value={licenseId}
                          onChange={(e) => {
                            onChange(e);
                            const imeiRegex = /^[0-9]{15}$/;
                            setIsValidImei(
                              imeiRegex.test(e.target.value) ||
                                e.target.value === ''
                            );
                          }}
                          autoComplete="off"
                        />
                        <small className="form-text">{t('IMEI')}</small>
                        {!isValidImei && (
                          <small style={{ color: 'red' }}>
                            {t('is_not_imei')}
                          </small>
                        )}
                      </div>

                      <div className="form-group">
                        <select
                          name="type"
                          value={type}
                          onChange={(e) => onChange(e)}
                          required
                        >
                          <option value="" disabled>
                            {t('type_select')}
                          </option>
                          <option value="unifield">Unifield</option>
                          <option value="support_access">Support Access</option>
                        </select>
                        <small className="form-text">{t('type_hint')}</small>
                      </div>

                      <div className="form-group">
                        <select
                          name="entity"
                          value={entity}
                          onChange={(e) => onChange(e)}
                          required
                        >
                          <option value="" disabled>
                            {t('entity_select')}
                          </option>
                          <Fragment>
                            <option value="admin">admin</option>
                            {entities.map((ent, index) => (
                              <option key={index} value={ent.name}>
                                {ent.name}
                              </option>
                            ))}
                          </Fragment>
                        </select>
                        <small className="form-text">{t('entity_hint')}</small>
                      </div>

                      <div className="form-group">
                        <input
                          type="date"
                          name="startDate"
                          value={startDate}
                          onChange={(e) => onChange(e)}
                        />
                        <small className="form-text">
                          {t('create_startDate')}
                        </small>
                      </div>
                      <div className="form-group">
                        <input
                          type="date"
                          name="endDate"
                          value={endDate}
                          onChange={(e) => onChange(e)}
                        />
                        <small className="form-text">
                          {t('create_endDate')}
                        </small>
                      </div>
                      <div
                        className="parent-flex"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          position: 'relative'
                        }}
                      >
                        <div className="form-group" style={{ flex: 1 }}>
                          <select
                            name="status"
                            value={status}
                            onChange={onChange}
                            required
                            className="form-control"
                          >
                            <option value="" disabled>
                              {t('status_select')}
                            </option>
                            <option value="activated">{t('activated')}</option>
                            <option value="disabled">{t('deactivated')}</option>
                            <option value="finished">{t('finished')}</option>
                            <option value="expired">{t('expired')}</option>
                          </select>
                          <small className="form-text text-muted">
                            {t('state')}
                          </small>
                        </div>

                        <div
                          onMouseEnter={() => setShowHelp(true)}
                          onMouseLeave={() => setShowHelp(false)}
                          style={{ position: 'relative' }}
                        >
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            title={t('help')}
                          >
                            <i
                              className="fa fa-question-circle"
                              style={{ fontSize: '1.2rem' }}
                            ></i>
                          </button>
                          {showHelp && (
                            <div
                              style={{
                                position: 'absolute',
                                bottom: '120%',
                                left: '50%',
                                transform: 'translateX(-100%)',
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #ced4da',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                zIndex: 10,
                                width: '200px',
                                height: 'auto'
                              }}
                            >
                              {t('help_text')}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="form-group">
                        <input
                          type="text"
                          placeholder={t('descriptionADV')}
                          name="descriptionADV"
                          value={descriptionADV}
                          onChange={(e) => onChange(e)}
                        />
                        <small className="form-text">
                          {t('descriptionADV_detail')}
                        </small>
                      </div>
                    </>
                  ) : (
                    <div className="form-group">
                      <input
                        type="text"
                        placeholder={t('descriptionAdminEntity_viewcustomer')}
                        name="descriptionAdminEntity"
                        value={descriptionAdminEntity}
                        onChange={onChange}
                      />
                      <small className="form-text">
                        {t('descriptionAdminEntity_detail')}
                      </small>
                    </div>
                  )}

                  {creatingLicense ? (
                    <>
                      <input
                        type="submit"
                        className="btn btn-primary my-1"
                        value={t('create')}
                      />
                      <Link className="btn btn-light my-1" to="/licenses">
                        {t('cancel')}
                      </Link>
                    </>
                  ) : (
                    <>
                      <input
                        type="submit"
                        className="btn btn-primary my-1"
                        value={t('save')}
                      />
                      <Link
                        className="btn btn-light my-1"
                        to={`/license/${license._id}`}
                      >
                        {t('cancel')}
                      </Link>
                    </>
                  )}
                </form>
              </Fragment>
            ) : (
              <p>{t('unauthorized_access')}</p>
            )}
          </Fragment>
        )}
      </Fragment>
    </section>
  );
};

LicenseEdit.propTypes = {
  createLicense: PropTypes.func.isRequired,
  getLicenseById: PropTypes.func.isRequired,
  getEntities: PropTypes.func.isRequired,
  license: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired
};

const mapStateToProps = (state) => ({
  entity: state.entity,
  license: state.license,
  auth: state.auth
});

export default connect(mapStateToProps, {
  createLicense,
  getLicenseById,
  getEntities
})(LicenseEdit);
